import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { Site, FortnoxIntegrationSettings as FortnoxIntegrationSettingsType } from '@/types';
import { apiService } from '@/utils/api';

interface FortnoxIntegrationSettingsProps {
  className?: string;
}

const FortnoxIntegrationSettings: React.FC<FortnoxIntegrationSettingsProps> = ({ className }) => {
  const queryClient = useQueryClient();
  const [selectedSiteId, setSelectedSiteId] = useState<number | null>(null);
  const [formData, setFormData] = useState<Partial<FortnoxIntegrationSettingsType>>({});
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);

  // Fetch sites
  const { data: sites = [], isLoading: sitesLoading } = useQuery(
    ['sites'],
    () => apiService.getSites()
  );

  // Set default site when sites are loaded
  useEffect(() => {
    if (sites.length > 0 && !selectedSiteId) {
      setSelectedSiteId(sites[0].id);
    }
  }, [sites, selectedSiteId]);

  // Fetch Fortnox integration settings
  const { data: fortnoxSettings, isLoading: settingsLoading } = useQuery(
    ['fortnox-integration-settings', selectedSiteId],
    () => apiService.getFortnoxIntegrationSettings(selectedSiteId!),
    {
      enabled: !!selectedSiteId,
    }
  );

  // Update form data when settings are loaded
  useEffect(() => {
    if (fortnoxSettings) {
      setFormData(fortnoxSettings);
    }
  }, [fortnoxSettings]);

  // Save mutation
  const saveMutation = useMutation(
    async (data: FortnoxIntegrationSettingsType) => {
      if (data.id) {
        return apiService.updateFortnoxIntegrationSettings(selectedSiteId!, data);
      } else {
        return apiService.createFortnoxIntegrationSettings(selectedSiteId!, data);
      }
    },
    {
      onSuccess: () => {
        setMessage({ type: 'success', text: 'Fortnox Integration Settings saved' });
        queryClient.invalidateQueries(['fortnox-integration-settings']);
        setTimeout(() => setMessage(null), 3000);
      },
      onError: () => {
        setMessage({ type: 'error', text: 'Something went wrong' });
        setTimeout(() => setMessage(null), 3000);
      },
    }
  );

  // Delete mutation
  const deleteMutation = useMutation(
    async () => {
      return apiService.deleteFortnoxIntegrationSettings(selectedSiteId!);
    },
    {
      onSuccess: () => {
        setMessage({ type: 'success', text: 'Fortnox Integration Settings deleted' });
        setFormData({});
        queryClient.invalidateQueries(['fortnox-integration-settings']);
        setTimeout(() => setMessage(null), 3000);
      },
      onError: () => {
        setMessage({ type: 'error', text: 'Something went wrong' });
        setTimeout(() => setMessage(null), 3000);
      },
    }
  );

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSiteChange = (siteId: number) => {
    setSelectedSiteId(siteId);
    setFormData({});
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedSiteId) return;
    
    const submitData: FortnoxIntegrationSettingsType = {
      ...formData,
      site_id: selectedSiteId,
    } as FortnoxIntegrationSettingsType;
    
    saveMutation.mutate(submitData);
  };

  const handleDelete = () => {
    if (window.confirm('Are you sure you want to delete this integration?')) {
      deleteMutation.mutate();
    }
  };

  const formatDateTime = (dateTime: string | null | undefined): string => {
    if (!dateTime) return '';
    try {
      return new Date(dateTime).toISOString().slice(0, 19).replace('T', ' ');
    } catch {
      return '';
    }
  };

  const parseDateTime = (dateTimeString: string): string => {
    if (!dateTimeString) return '';
    try {
      return new Date(dateTimeString).toISOString().slice(0, 19).replace('T', ' ');
    } catch {
      return '';
    }
  };

  const isLoading = sitesLoading || settingsLoading;

  return (
    <div className={`container mx-auto px-4 py-8 ${className || ''}`}>
      <h1 className="section-title">Fortnox Integration Settings</h1>
      
      {/* Loading Alert */}
      {isLoading && (
        <div className="alert alert-info">
          <span>Loading...</span>
        </div>
      )}

      {/* Success/Error Messages */}
      {message && (
        <div className={`alert ${message.type === 'success' ? 'alert-success' : message.type === 'error' ? 'alert-danger' : 'alert-info'}`}>
          <span>{message.text}</span>
        </div>
      )}

      <div className="card">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Site Selection */}
          <div>
            <label className="form-label">Site ID:</label>
            <select
              value={selectedSiteId || ''}
              onChange={(e) => handleSiteChange(parseInt(e.target.value))}
              className="form-input"
              disabled={isLoading}
            >
              {sites.map((site: Site) => (
                <option key={site.id} value={site.id}>
                  {site.url}
                </option>
              ))}
            </select>
          </div>

          {/* Checkboxes Row */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="flex items-center space-x-3">
              <input
                type="checkbox"
                id="order_create"
                checked={formData.order_create || false}
                onChange={(e) => handleInputChange('order_create', e.target.checked)}
                className="w-5 h-5 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
                disabled={isLoading}
              />
              <label htmlFor="order_create" className="text-sm font-medium text-gray-700">
                Create Orders
              </label>
            </div>

            <div className="flex items-center space-x-3">
              <input
                type="checkbox"
                id="product_create"
                checked={formData.product_create || false}
                onChange={(e) => handleInputChange('product_create', e.target.checked)}
                className="w-5 h-5 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
                disabled={isLoading}
              />
              <label htmlFor="product_create" className="text-sm font-medium text-gray-700">
                Create Products Woo → Fortnox
              </label>
            </div>

            <div className="flex items-center space-x-3">
              <input
                type="checkbox"
                id="update_stock"
                checked={formData.update_stock || false}
                onChange={(e) => handleInputChange('update_stock', e.target.checked)}
                className="w-5 h-5 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
                disabled={isLoading}
              />
              <label htmlFor="update_stock" className="text-sm font-medium text-gray-700">
                Update Products Fortnox → Woo
              </label>
            </div>
          </div>

          {/* Order Prefix */}
          <div>
            <label className="form-label">Order prefix:</label>
            <input
              type="text"
              value={formData.order_prefix || ''}
              onChange={(e) => handleInputChange('order_prefix', e.target.value)}
              className="form-input"
              disabled={isLoading}
            />
          </div>

          {/* DateTime Fields */}
          <div className="space-y-4">
            <div>
              <label className="form-label">Latest time Create Orders Woo→Fortnox:</label>
              <input
                type="datetime-local"
                value={formatDateTime(formData.order_create_latest_poll_time)}
                onChange={(e) => handleInputChange('order_create_latest_poll_time', parseDateTime(e.target.value))}
                className="form-input"
                disabled={isLoading}
              />
            </div>

            <div>
              <label className="form-label">Latest time Update Products Woo→Fortnox:</label>
              <input
                type="datetime-local"
                value={formatDateTime(formData.product_create_latest_poll_time)}
                onChange={(e) => handleInputChange('product_create_latest_poll_time', parseDateTime(e.target.value))}
                className="form-input"
                disabled={isLoading}
              />
            </div>

            <div>
              <label className="form-label">Latest time Stock Update Fortnox→Woo:</label>
              <input
                type="datetime-local"
                value={formatDateTime(formData.update_stock_latest_poll_time)}
                onChange={(e) => handleInputChange('update_stock_latest_poll_time', parseDateTime(e.target.value))}
                className="form-input"
                disabled={isLoading}
              />
            </div>

            <div>
              <label className="form-label">Latest time Invoice Create:</label>
              <input
                type="datetime-local"
                value={formatDateTime(formData.invoice_create_latest_poll_time)}
                onChange={(e) => handleInputChange('invoice_create_latest_poll_time', parseDateTime(e.target.value))}
                className="form-input"
                disabled={isLoading}
              />
            </div>

            <div>
              <label className="form-label">Last time price update:</label>
              <input
                type="datetime-local"
                value={formatDateTime(formData.price_update_latest_poll_time)}
                onChange={(e) => handleInputChange('price_update_latest_poll_time', parseDateTime(e.target.value))}
                className="form-input"
                disabled={isLoading}
              />
            </div>

            <div>
              <label className="form-label">Latest time Order delivery state Listen Fortnox→Woo:</label>
              <input
                type="datetime-local"
                value={formatDateTime(formData.order_delivery_state_listen_latest_poll_time)}
                onChange={(e) => handleInputChange('order_delivery_state_listen_latest_poll_time', parseDateTime(e.target.value))}
                className="form-input"
                disabled={isLoading}
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end pt-6 space-x-3">
            <button
              type="submit"
              className="px-4 py-2 text-white rounded-md hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50"
              style={{ backgroundColor: 'hsl(15 100% 52%)' }}
              disabled={isLoading || saveMutation.isLoading}
            >
              {saveMutation.isLoading ? 'Saving...' : 'Save'}
            </button>
            
            {formData.id && (
              <button
                type="button"
                onClick={handleDelete}
                className="btn btn-danger"
                disabled={isLoading || deleteMutation.isLoading}
              >
                {deleteMutation.isLoading ? 'Deleting...' : 'Delete'}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default FortnoxIntegrationSettings;
